import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Upload } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAstrologerProfile } from '../../hooks/useAstrologerProfile';
import type { AstrologerClient, AstrologerAnalysis } from '../../types/professional';

function clientSunSign(client: AstrologerClient): string {
  return client.natal_chart_json?.sun_sign ?? '';
}

export function ProfessionalClientList() {
  const navigate = useNavigate();
  const { astrologerProfile, loading } = useAstrologerProfile();
  const [clients,    setClients]    = useState<AstrologerClient[]>([]);
  const [filtered,   setFiltered]   = useState<AstrologerClient[]>([]);
  const [search,     setSearch]     = useState('');
  const [sortBy,     setSortBy]     = useState<'recent' | 'name' | 'birth'>('recent');
  const [loadingData, setLoadingData] = useState(true);
  // Map of client2_id → { analysisId, client1Id } for synastry link display
  const [synastryMap, setSynastryMap] = useState<Record<string, { analysisId: string; client1Id: string }>>({});

  useEffect(() => {
    if (!astrologerProfile) {
      if (!loading) setLoadingData(false);
      return;
    }
    if (!astrologerProfile.wizard_completed) { navigate('/profissional/ativar'); return; }

    Promise.all([
      supabase
        .from('astrologer_clients')
        .select('*')
        .eq('astrologer_id', astrologerProfile.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false }),
      supabase
        .from('astrologer_client_analyses')
        .select('id, client_id, client2_id, service_slug')
        .eq('astrologer_id', astrologerProfile.id)
        .in('service_slug', ['synastry', 'astromatch'])
        .not('client2_id', 'is', null),
    ]).then(([clientsRes, analysesRes]) => {
      setClients((clientsRes.data ?? []) as AstrologerClient[]);
      const map: Record<string, { analysisId: string; client1Id: string }> = {};
      for (const a of (analysesRes.data ?? []) as AstrologerAnalysis[]) {
        if (a.client2_id) map[a.client2_id] = { analysisId: a.id, client1Id: a.client_id };
      }
      setSynastryMap(map);
      setLoadingData(false);
    });
  }, [astrologerProfile?.id, loading, navigate]);

  useEffect(() => {
    let list = [...clients];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c => c.name.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q));
    }
    if (sortBy === 'name')   list.sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === 'birth')  list.sort((a, b) => a.birth_date.localeCompare(b.birth_date));
    setFiltered(list);
  }, [clients, search, sortBy]);

  if (loading || loadingData) return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>Carregando clientes...</div>;
  if (!astrologerProfile) return null;

  const thisMonth = new Date().getMonth() + 1;
  const birthdayClients = clients.filter(c => {
    const m = new Date(c.birth_date + 'T00:00:00').getMonth() + 1;
    return m === thisMonth;
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: 'clamp(1.25rem,3vw,1.625rem)', color: 'var(--text-primary)', margin: 0, fontFamily: 'var(--font-display)' }}>
            Meus Clientes
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: '0.25rem 0 0' }}>
            {clients.length} cliente{clients.length !== 1 ? 's' : ''}
            {birthdayClients.length > 0 && ` · ${birthdayClients.length} aniversariante${birthdayClients.length !== 1 ? 's' : ''} este mês`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn-secondary" style={{ fontSize: 'var(--text-caption)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
            onClick={() => navigate('/profissional/clientes/importar')}>
            <Upload size={14} /> Importar CSV
          </button>
          <button className="btn-primary" style={{ fontSize: 'var(--text-caption)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
            onClick={() => navigate('/profissional/clientes/novo')}>
            <Plus size={14} /> Novo cliente
          </button>
        </div>
      </div>

      {/* Search + sort */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 240px' }}>
          <Search size={15} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            className="form-input"
            placeholder="Buscar por nome ou e-mail..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: '2.25rem', width: '100%' }}
          />
        </div>
        <select
          className="form-input"
          value={sortBy}
          onChange={e => setSortBy(e.target.value as typeof sortBy)}
          style={{ width: 'auto', fontSize: 'var(--text-caption)' }}
        >
          <option value="recent">Mais recentes</option>
          <option value="name">Nome A→Z</option>
          <option value="birth">Data de nascimento</option>
        </select>
      </div>

      {/* Client cards */}
      {filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ color: 'var(--text-muted)' }}>
            {search ? 'Nenhum cliente encontrado para esta busca.' : 'Nenhum cliente cadastrado ainda.'}
          </p>
          {!search && (
            <button className="btn-primary" style={{ marginTop: '1rem' }} onClick={() => navigate('/profissional/clientes/novo')}>
              Cadastrar primeiro cliente
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map(client => {
            const sunSign = clientSunSign(client);
            const moonSign = client.natal_chart_json?.moon_sign ?? '';
            const birthDateFmt = new Date(client.birth_date + 'T00:00:00').toLocaleDateString('pt-BR');
            return (
              <div key={client.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                {/* Avatar */}
                <div style={{
                  width: '42px', height: '42px', borderRadius: '50%', flexShrink: 0,
                  background: 'linear-gradient(135deg, var(--petrol), var(--petrol))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '1rem', color: '#000',
                }}>
                  {client.name.charAt(0).toUpperCase()}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: '0 0 0.15rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>
                    {client.name}
                  </p>
                  <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                    {birthDateFmt}
                    {client.birth_city ? ` · ${client.birth_city}` : ''}
                    {sunSign ? ` · Sol em ${sunSign}` : ''}
                    {moonSign ? ` · Lua em ${moonSign}` : ''}
                  </p>
                  {synastryMap[client.id] && (() => {
                    const { analysisId, client1Id } = synastryMap[client.id];
                    const partner = clients.find(c => c.id === client1Id);
                    return (
                      <p style={{ margin: '0.25rem 0 0', fontSize: 'var(--text-caption)', color: 'var(--petrol)' }}>
                        ♥ Sinastria com{' '}
                        <button
                          style={{ background: 'none', border: 'none', color: 'var(--petrol)', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 'inherit' }}
                          onClick={() => navigate(`/profissional/clientes/${client1Id}?tab=analises&highlight=${analysisId}`)}
                        >
                          {partner?.name ?? 'cliente'}
                        </button>
                      </p>
                    );
                  })()}
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                  <button
                    className="btn-secondary"
                    style={{ fontSize: 'var(--text-caption)', padding: '0.3rem 0.75rem' }}
                    onClick={() => navigate(`/profissional/clientes/${client.id}`)}
                  >
                    Ver perfil
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
